'use client'

import React, { useEffect } from 'react'

import { Box, Container, Typography, Card, Grid } from '@mui/material'
import { styled } from '@mui/material/styles'
import AOS from 'aos'
import 'aos/dist/aos.css'

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 32,
  overflow: 'hidden',
  height: '100%',
  background: 'rgba(255,255,255,0.92)',
  boxShadow: '0 12px 40px 0 rgba(33,150,243,0.10), 0 2px 8px 0 rgba(0,0,0,0.04)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'transform 0.35s cubic-bezier(.4,2,.6,1), box-shadow 0.35s cubic-bezier(.4,2,.6,1)',
  backdropFilter: 'blur(6px)',
  border: '1.5px solid rgba(33,150,243,0.10)',
  '&:hover': {
    transform: 'scale(1.045) translateY(-4px)',
    boxShadow: '0 20px 48px 0 rgba(33,150,243,0.16), 0 4px 16px 0 rgba(0,0,0,0.08)'
  }
}))

const StyledImage = styled('img')({
  width: '38dvh',
  height: '38dvh',
  objectFit: 'cover',
  borderRadius: '50%',
  border: '5px solid #fff',
  boxShadow: '0 8px 32px rgba(33,150,243,0.10)',
  transition: 'transform 0.4s cubic-bezier(.4,2,.6,1)',
  background: 'linear-gradient(135deg, #e3f0ff 0%, #f9f9f9 100%)',
  '&:hover': {
    transform: 'scale(1.09) rotate(-2deg)'
  }
})

const StyledIcon = styled('img')(({ theme }) => ({
  width: theme.spacing(7),
  height: theme.spacing(7),
  objectFit: 'cover',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #e3f0ff 0%, #f9f9f9 100%)',
  boxShadow: '0 4px 16px rgba(33,150,243,0.10)',
  padding: theme.spacing(1.2),
  border: `2.5px solid ${theme.palette.primary.light}`,
  transition: 'transform 0.35s cubic-bezier(.4,2,.6,1)',
  [theme.breakpoints.down('sm')]: {
    width: theme.spacing(5),
    height: theme.spacing(5)
  },
  '&:hover': {
    transform: 'scale(1.13) rotate(-8deg)'
  }
}))

const About = () => {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true })
  }, [])

  return (
    <Box
      component='section'
      sx={{
        py: { xs: 7, md: 12 },
        background: 'linear-gradient(120deg, #e3f0ff 0%, #f9f9f9 100%)',
        minHeight: '100vh',
        position: 'relative'
      }}
      id='about'
      aria-labelledby='about-title'
    >
      <Container maxWidth='lg'>
        <Typography
          id='about-title'
          data-aos={'fade-down'}
          variant='h2'
          component='h2'
          gutterBottom
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '2rem', md: '2.5rem' },
            color: 'text.primary',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60px',
              height: '4px',
              backgroundColor: 'primary.main',
              borderRadius: '2px'
            }
          }}
        >
          Tentang Kami
        </Typography>

        <Grid container spacing={{ xs: 6, md: 8 }} alignItems='center'>
          <Grid item xs={12} md={5} data-aos={'fade-right'}>
            <StyledCard elevation={0}>
              <StyledImage
                src='/images/icon/gambar_1.webp'
                alt='TMC Eternal Home - Rumah Duka Modern dan Profesional'
                loading='lazy'
              />
            </StyledCard>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box sx={{ pl: { md: 4 }, pr: { xs: 0, md: 2 } }}>
              <Box sx={{ mb: 4 }} data-aos={'fade-down'}>
                <Typography
                  variant='h3'
                  gutterBottom
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.3rem', md: '2rem' },
                    color: 'primary.main',
                    mb: 2,
                    letterSpacing: 1.2,
                    textShadow: '0 2px 8px rgba(33,150,243,0.10)'
                  }}
                >
                  TMC Eternal Home
                </Typography>
                <Typography
                  variant='h5'
                  gutterBottom
                  sx={{
                    fontWeight: 500,
                    color: 'text.secondary',
                    mb: 3
                  }}
                >
                  Melayani dengan Penuh Kasih dan Kehormatan
                </Typography>
                <Typography
                  variant='body1'
                  paragraph
                  sx={{
                    lineHeight: 1.8,
                    fontSize: '1.13rem',
                    color: 'text.primary',
                    mb: 2
                  }}
                >
                  Rumah Duka kami hadir untuk memberikan pelayanan terbaik dalam mendampingi keluarga yang sedang
                  berduka. Dengan pemahaman yang mendalam dan tim yang sudah terbiasa menangani keluarga dalam masa
                  berduka, kami berkomitmen untuk memberikan pelayanan yang penuh empati, modern dan profesional, serta
                  sesuai dengan nilai-nilai keagamaan dan budaya keluarga.
                </Typography>
                <Box
                  sx={{
                    width: 70,
                    height: 4,
                    background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                    borderRadius: 2,
                    mb: 2,
                    boxShadow: '0 2px 8px rgba(33,150,243,0.10)'
                  }}
                />
              </Box>
              <Grid container spacing={3} data-aos={'fade-left'}>
                {/* Vision Section */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2.5,
                      p: 3.5,
                      bgcolor: 'rgba(255,255,255,0.98)',
                      borderRadius: 4,
                      boxShadow: '0 8px 32px 0 rgba(33,150,243,0.10), 0 2px 8px 0 rgba(0,0,0,0.04)',
                      alignItems: 'flex-start',
                      minHeight: 180,
                      border: '1.5px solid rgba(33,150,243,0.10)',
                      position: 'relative',
                      transition: 'box-shadow 0.35s cubic-bezier(.4,2,.6,1), transform 0.35s cubic-bezier(.4,2,.6,1)',
                      '&:hover': {
                        boxShadow: '0 20px 48px 0 rgba(33,150,243,0.16), 0 4px 16px 0 rgba(0,0,0,0.08)',
                        transform: 'translateY(-4px) scale(1.03)'
                      }
                    }}
                  >
                    <StyledIcon src='/images/icon/opportunity.png' alt='Visi Kami' />
                    <Box>
                      <Typography
                        variant='h5'
                        gutterBottom
                        sx={{ fontWeight: 800, color: 'primary.main', mb: 1, letterSpacing: 0.5 }}
                      >
                        Visi Kami
                      </Typography>
                      <Typography variant='body1' sx={{ lineHeight: 1.8, color: 'text.primary', fontSize: '1.08rem' }}>
                        Menjadi rumah duka terpercaya yang memberikan pelayanan berkualitas dengan penuh kehormatan dan
                        ketulusan kepada setiap keluarga yang berduka.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 18,
                        right: 18,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        opacity: 0.18
                      }}
                    />
                  </Box>
                </Grid>
                {/* Mission Section */}
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2.5,
                      p: 3.5,
                      bgcolor: 'rgba(255,255,255,0.98)',
                      borderRadius: 4,
                      boxShadow: '0 8px 32px 0 rgba(33,150,243,0.10), 0 2px 8px 0 rgba(0,0,0,0.04)',
                      alignItems: 'flex-start',
                      minHeight: 180,
                      border: '1.5px solid rgba(33,150,243,0.10)',
                      position: 'relative',
                      transition: 'box-shadow 0.35s cubic-bezier(.4,2,.6,1), transform 0.35s cubic-bezier(.4,2,.6,1)',
                      '&:hover': {
                        boxShadow: '0 20px 48px 0 rgba(33,150,243,0.16), 0 4px 16px 0 rgba(0,0,0,0.08)',
                        transform: 'translateY(-4px) scale(1.03)'
                      }
                    }}
                  >
                    <StyledIcon src='/images/icon/target.png' alt='Misi Kami' />
                    <Box>
                      <Typography
                        variant='h5'
                        gutterBottom
                        sx={{ fontWeight: 800, color: 'primary.main', mb: 1, letterSpacing: 0.5 }}
                      >
                        Misi Kami
                      </Typography>
                      <Box component='ul' sx={{ pl: 2, m: 0 }}>
                        {[
                          'Memberikan pelayanan yang modern dan profesional serta penuh empati',
                          'Menjaga kehormatan dan martabat almarhum/almarhumah',
                          'Mendampingi keluarga dalam proses pengurusan pemakaman',
                          'Terus berinovasi dalam pelayanan rumah duka yang modern dan profesional'
                        ].map((item, index) => (
                          <Typography
                            key={index}
                            component='li'
                            variant='body1'
                            sx={{
                              mb: 1,
                              lineHeight: 1.8,
                              color: 'text.primary',
                              fontSize: '1.08rem',
                              '&::marker': {
                                color: 'primary.main'
                              }
                            }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 18,
                        right: 18,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        opacity: 0.18
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default About
